import { Dialog, Tab, Transition } from '@headlessui/react'
import React, { Fragment, useEffect, useState } from 'react'
import { BsBank, BsBank2 } from 'react-icons/bs'
import { FiChevronDown, FiEdit, FiTrash2 } from 'react-icons/fi'
import { IoClose, IoAddSharp } from 'react-icons/io5'
import { useGetBanksQuery } from '../../../features/account/bankSlice'
import { Loader } from '../../../shared/Loader'
import { SuccessIcon } from '../../../shared/SuccessIcon'
import { BankAccount } from './BankAccount'
import { CryptoAccount } from './CryptoAccount'
import { DeleteAccountModal } from './DeleteAccountModal'
import { IBank } from './types'

export const PaymentDetails = () => {
  const [accounts, setAccounts] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState<'init' | 'Success'>('init')

  const { data: banks, isLoading, isError } = useGetBanksQuery()

  function _renderStepContent(step: string) {
    switch (step) {
      case 'init':
        return (
          <div className='flex flex-col items-center gap-2 mt-5'>
            <div className='mt-3 w-full max-w-sm'>
              <BankAccount setCurrentStep={setCurrentStep} />
            </div>
          </div>
        )

      case 'Success':
        return (
          <div className='relative py-24 flex flex-col items-center'>
            <div className='flex flex-col items-center gap-4'>
              <SuccessIcon />
              <p className='text-xl font-light text-white/40'>
                Account creation successful
              </p>
            </div>

            <button
              className='btn-primary py-2 rounded-lg w-full max-w-md mt-12'
              onClick={async () => {
                setIsModalOpen(false)
                setTimeout(() => {
                  setCurrentStep('init')
                }, 1000)
              }}
            >
              Done
            </button>
          </div>
        )
    }
  }

  return (
    <div className='w-full relative py-8'>
      {isLoading ? (
        <div className='w-full h-[70vh] flex justify-center items-center'>
          <Loader />
        </div>
      ) : banks?.data?.length > 0 ? (
        <div className='w-full md:px-4 py-5 md:py-3 bg-black'>
          <div className='flex items-center justify-between pr-5'>
            <h1 className='text-xl text-primary font-semibold pl-5'>
              All accounts
            </h1>

            <button
              className='transition-all duration-200 flex items-center gap-2 rounded-full px-4 py-2 bg-white/90 hover:bg-white hover:text-black text-sm'
              onClick={() => setIsModalOpen(true)}
            >
              <span>
                <IoAddSharp className='text-lg text-black' />
              </span>{' '}
              Add new account
            </button>
          </div>
          <div className='mx-5 mt-4 rounded-lg border border-primary/40 bg-gray-700/40 px-4 py-3'>
            <p className='text-sm text-white/80'>
              For crypto withdrawals, kindly click Withdrawals and select the{' '}
              <strong className='font-semibold text-primary'>
                Quick Crypto Payout route
              </strong>
              .
            </p>
          </div>
          <div className='overflow-x-auto'>
            <table className='relative mt-8 w-full text-sm text-left text-gray-400'>
              <thead className='text-xs uppercase bg-gray-700 text-gray-300'>
                <tr>
                  <th scope='col' className='py-3 px-6'>
                    Bank Name
                  </th>
                  <th scope='col' className='py-3 px-6'>
                    Account Name
                  </th>
                  <th scope='col' className='py-3 px-6'>
                    Account Number
                  </th>
                  <th scope='col' className='py-3 px-6'></th>
                </tr>
              </thead>
              <tbody>
                {banks?.data?.map((bank: IBank) => (
                  <tr className='border-b bg-transparent border-gray-700'>
                    <th
                      scope='row'
                      className='py-4 px-6 font-medium whitespace-nowrap text-white'
                    >
                      {bank.bankName}
                    </th>

                    <td className='py-4 px-6'>{bank.accountName}</td>
                    <td className='py-4 px-6'>{`${bank.accountNumber.substring(
                      0,
                      2
                    )}*****${bank.accountNumber
                      .toString()
                      .substring(bank.accountNumber.length - 4)}`}</td>
                    <td>
                      <div className='flex items-center gap-2'>
                        <DeleteAccountModal bank={bank} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className='max-w-md mx-auto flex justify-center items-center pt-20'>
          <div className='flex flex-col items-center gap-2'>
            <BsBank2 className='box-content p-4 rounded-md text-5xl bg-gray-600 text-white/20' />

            <p className='text-sm text-white/30 font-light text-center'>
              Add a new payment details
            </p>

            <button
              className='transition-all duration-200 border border-white/80 rounded-full w-64 px-4 py-3 hover:bg-white/80 text-white/60 hover:text-black text-sm mt-4'
              onClick={() => setIsModalOpen(true)}
            >
              Add Account
            </button>
          </div>
        </div>
      )}

      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as='div'
          className='relative z-50'
          onClose={() => setIsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter='ease-out duration-300'
            enterFrom='opacity-0'
            enterTo='opacity-100'
            leave='ease-in duration-200'
            leaveFrom='opacity-100'
            leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-black/40 backdrop-blur-sm' />
          </Transition.Child>

          <div className='fixed inset-0 overflow-y-auto'>
            <div className='flex min-h-full items-center justify-center p-4 text-center'>
              <Transition.Child
                as={Fragment}
                enter='ease-out duration-300'
                enterFrom='opacity-0 scale-95'
                enterTo='opacity-100 scale-100'
                leave='ease-in duration-200'
                leaveFrom='opacity-100 scale-100'
                leaveTo='opacity-0 scale-95'
              >
                <Dialog.Panel className='relative font-dashboard w-full max-w-3xl min-h-[500px] transform overflow-hidden rounded-2xl bg-[#161819] px-4 pt-4 pb-10 text-left align-middle shadow-xl transition-all'>
                  <div className='relative pt-2 pb-4'>
                    <h3 className='text-xl text-white/40 font-light text-center'>
                      Account
                    </h3>
                    <IoClose
                      className='text-2xl text-white absolute right-3 top-3 cursor-pointer'
                      onClick={() => setIsModalOpen(false)}
                    />
                  </div>
                  <hr className='border-white/10' />

                  {_renderStepContent(currentStep)}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}
